/* eslint-disable @typescript-eslint/no-explicit-any */
import { yupResolver } from "@hookform/resolvers/yup";
import Button from "components/Button";
import Input from "components/Input";
import Modal from "components/Modal";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import apiService from "services/apiService";
import { useToaster } from "contexts/toasterContext";
import * as yup from "yup";
import { preventSpecialCharKeyAndNumbersDownInTextfield, preventSpecialCharKeyDownInTextfield } from "util/preventSpecialCharInput";
import { EditButton, EmptyState, InfoCard, InfoField, InfoFieldGrid } from "./components/ProfileInfo";
import { useAppContext } from "contexts/useAppContext";
import { isUserRole, parseJwtPayload } from "util/moduleAccess";
import {  validateIFSCCode } from "util/validation";

interface BankDetailsProps {
  user: any;
  onUserUpdated?: (user: any) => void;
}

interface BankDetailsData {
  id?: string;
  employeeId?: string;
  bankName: string;
  accountNumber: string;
  ifscCode: string;
  accountHolderName: string;
  branchName: string;
  accountType: string;
}

const validationSchema = yup.object({
  bankName: yup
    .string()
    .trim()
    .defined()
    .required("Bank Name is required"),
  accountNumber: yup
    .string()
    .trim()
    .defined()
    .required("Account Number is required")
    .matches(/^[0-9]+$/, "Account Number must contain only numbers"),
  ifscCode: yup
    .string()
    .trim()
    .defined()
    .required("IFSC Code is required")
    .length(11, "IFSC Code must be exactly 11 characters")
    .test(
      "is-valid-ifsc",
      "Invalid IFSC Code format (e.g., SBIN0123456)",
      (value) => validateIFSCCode(value)
    ),
  accountHolderName: yup
    .string()
    .trim()
    .defined()
    .required("Account Holder Name is required")
    .matches(/^[a-zA-Z\s]+$/, "Only alphabets are allowed for this field"),
  branchName: yup
    .string()
    .trim()
    .defined()
    .required("Branch Name is required"),
  accountType: yup
    .string()
    .trim()
    .defined()
    .required("Account Type is required")
    .matches(/^[a-zA-Z\s]+$/, "Only alphabets are allowed for this field"),
}).required();

type BankDetailsFormData = yup.InferType<typeof validationSchema>;

const defaultBankDetails: BankDetailsFormData = {
  bankName: "",
  accountNumber: "",
  ifscCode: "",
  accountHolderName: "",
  branchName: "",
  accountType: "",
};

const BANK_FIELDS = Object.keys(defaultBankDetails) as (keyof BankDetailsFormData)[];

const hasBankData = (details?: Partial<BankDetailsData> | null) =>
  BANK_FIELDS.some((field) => String(details?.[field] ?? "").trim().length > 0);

const toFormData = (
  details?: Partial<BankDetailsData> | null,
): BankDetailsFormData => ({
  bankName: details?.bankName ?? "",
  accountNumber: details?.accountNumber ?? "",
  ifscCode: details?.ifscCode ?? "",
  accountHolderName: details?.accountHolderName ?? "",
  branchName: details?.branchName ?? "",
  accountType: details?.accountType ?? "",
});

const BankDetails = ({ user, onUserUpdated }: Readonly<BankDetailsProps>) => {
  const [bankDetails, setBankDetails] = useState<BankDetailsFormData | null>(null);
  const [userData, setUserData] = useState<any>(user);
  const [loading, setLoading] = useState<boolean>(true);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isAddeding, setIsAdding] = useState<boolean>(false);
  const { addToast } = useToaster();
  const { userRole } = useAppContext();
  const canEditBankDetails = !isUserRole(
    parseJwtPayload(localStorage.getItem("accessToken")),
    userRole ?? user?.role
  );

  // Use schema validation for required fields and inline error states
  const methods = useForm<BankDetailsFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: defaultBankDetails,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting },
  } = methods;

  useEffect(() => {
    if (user?.id) {
      fetchBankDetails();
    }
  }, [user?.id]);

  const fetchBankDetails = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`api/user/me`);
      console.log("Bank Details Response: ", response.data.user);

      // Store the complete API response
      if (response.data.user) {
        setUserData(response.data.user);
      }

      if (hasBankData(response.data.user?.profileDetails)) {
        // Normalize API data into strict form shape
        const normalizedDetails = toFormData(response.data.user.profileDetails);
        setBankDetails(normalizedDetails);
        reset(normalizedDetails);
      } else {
        setBankDetails(null);
        // Reset form when no bank details exist
        reset(defaultBankDetails);
      }
    } catch (error) {
      console.error("Error fetching bank details:", error);
      setBankDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: BankDetailsFormData) => {
    const sanitizedData: BankDetailsFormData = {
      ...data,
      bankName: data.bankName.trim(),
      accountNumber: data.accountNumber.trim(),
      ifscCode: data.ifscCode.trim(),
      accountHolderName: data.accountHolderName.trim(),
      branchName: data.branchName.trim(),
      accountType: data.accountType.trim(),
    };

    try {
      const latestUserResponse = await apiService.get(`api/user/me`);
      const latestUser = latestUserResponse?.data?.user || userData;
      const isUpdate = hasBankData(latestUser?.profileDetails);
      const payload = {
        ...latestUser,
        profileDetails: {
          ...(latestUser?.profileDetails || {}),
          ...sanitizedData,
        },
      };

      const response = await apiService.put(`api/user`, payload);
      addToast(
        isUpdate ? "Bank details updated successfully" : "Bank details added successfully",
        "success"
      );

      // Immediately update state with the submitted data
      setBankDetails(sanitizedData);

      setUserData(response?.data || payload);
      onUserUpdated?.(payload);

      // Wait for data to refresh from server
      await fetchBankDetails();

      // Reset form with latest saved values (clean baseline)
      reset(sanitizedData);
      setIsEditing(false);
      setIsAdding(false); // Close add form if it was open
    } catch (error) {
      console.error("Error saving bank details:", error);
      addToast("Failed to save bank details", "error");
    }
  };

  const handleAddClick = () => {
    // Clear all form fields
    reset(defaultBankDetails);
    setIsAdding(true);
  };

  const handleUpdateClick = () => {
    if (bankDetails) {
      // Set update mode baseline values so unchanged form remains non-dirty
      reset(toFormData(bankDetails));
    }
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    if (bankDetails) {
      reset(toFormData(bankDetails));
    } else {
      reset(defaultBankDetails);
    }
    setIsEditing(false);
    setIsAdding(false);
  };

  if (loading) {
    return (
      <InfoCard title="Bank Details">
        <div className="flex justify-center items-center py-8 text-sm text-gray-600 dark:text-gray-300">
          Loading...
        </div>
      </InfoCard>
    );
  }

  const showEmptyState = !bankDetails && !hasBankData(userData?.profileDetails);

  return (
    <>
      {showEmptyState ? (
        <InfoCard
          title="Bank Details"
          action={<EditButton label="Add" onClick={handleAddClick} />}
        >
          <EmptyState message="No bank details available" />
        </InfoCard>
      ) : (
        <InfoCard
          title="Bank Details"
          action={canEditBankDetails && <EditButton onClick={handleUpdateClick} />}
        >
          <InfoFieldGrid>
            <InfoField label="Bank Name" value={<span className="capitalize">{bankDetails?.bankName}</span>} />
            <InfoField label="Account Number" value={<span className="font-mono tracking-wider">{bankDetails?.accountNumber}</span>} />
            <InfoField label="IFSC Code" value={<span className="font-mono uppercase tracking-widest">{bankDetails?.ifscCode}</span>} />
            <InfoField label="Account Holder Name" value={<span className="capitalize">{bankDetails?.accountHolderName}</span>} />
            <InfoField label="Branch Name" value={<span className="capitalize">{bankDetails?.branchName}</span>} />
            <InfoField label="Account Type" value={<span className="capitalize">{bankDetails?.accountType || "Not specified"}</span>} />
          </InfoFieldGrid>
        </InfoCard>
      )}

      {isAddeding && (
        <Modal
          isOpen={isAddeding}
          onClose={handleCancelClick}
          header="Add Bank Details"
          // custemHeight="700px"
          // customWidth="700px"
          size="md"
        >
          <FormProvider {...methods}>
            <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
              <div className="sm:grid grid-cols-2 gap-4">
                <Input
                  name="bankName"
                  label="Bank Name"
                  placeholder="Enter Bank Name"
                  required
                  onKeyDown={preventSpecialCharKeyDownInTextfield}
                  validationSchema={yup.string().trim().required("Bank Name is required")}
                />
                <Input
                  name="accountNumber"
                  label="Account Number"
                  placeholder="Enter Account Number"
                  required
                  type="number"
                  disableAutoCapitalize
                  validationSchema={yup
                    .string()
                    .trim()
                    .required("Account Number is required")
                    .matches(/^[0-9]+$/, "Account Number must contain only numbers")}
                />
                <Input
                  name="ifscCode"
                  label="IFSC Code"
                  placeholder="Enter IFSC Code"
                  required
                  disableAutoCapitalize
                  validationSchema={yup.string().trim().required("IFSC Code is required")}
                />
                <Input
                  name="accountHolderName"
                  label="Account Holder Name"
                  placeholder="Enter Account Holder Name"
                  required
                  onKeyDown={preventSpecialCharKeyDownInTextfield}
                  validationSchema={yup
                    .string()
                    .trim()
                    .required("Account Holder Name is required")}
                />
                <Input
                  name="branchName"
                  label="Branch Name"
                  placeholder="Enter Branch Name"
                  onKeyDown={preventSpecialCharKeyDownInTextfield}
                  required
                  validationSchema={yup.string().trim().required("Branch Name is required")}
                />
                <Input
                  name="accountType"
                  label="Account Type"
                  placeholder="Savings/Current"
                  required
                  onKeyDown={preventSpecialCharKeyDownInTextfield}
                  validationSchema={yup.string().trim().required("Account Type is required")}
                />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={handleCancelClick}
                  className="text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-900 focus:ring-4 focus:outline-none focus:ring-gray-200 font-medium rounded-lg text-sm px-5 py-2.5 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600 dark:hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <Button className={`text-sm font-medium rounded-lg px-5 py-2.5 focus:ring-4 focus:outline-none focus:ring-blue-300 transition-colors ${(!!bankDetails && !isDirty) || isSubmitting
                  ? "text-white bg-blue-400 cursor-not-allowed opacity-70"
                  : "text-white bg-blue-700 hover:bg-blue-800"
                  }`} type="submit" disabled={isSubmitting}>Add</Button>
              </div>
            </form>
          </FormProvider>
        </Modal>
      )}

      {isEditing && (
        <Modal
          isOpen={isEditing}
          onClose={handleCancelClick}
          header="Edit Bank Details"
          // custemHeight="700px"
          // customWidth="700px"
          size="md"
        >
          <FormProvider {...methods}>
            <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
              <div className="sm:grid grid-cols-2 gap-4">
                <Input
                  name="bankName"
                  label="Bank Name"
                  placeholder="Enter Bank Name"
                  required
                  validationSchema={yup.string().trim().required("Bank Name is required")}
                  onKeyDown={preventSpecialCharKeyAndNumbersDownInTextfield}
                />
                <Input
                  name="accountNumber"
                  label="Account Number"
                  placeholder="Enter Account Number"
                  required
                  type="number"
                  disableAutoCapitalize
                  validationSchema={yup
                    .string()
                    .trim()
                    .required("Account Number is required")
                    .matches(/^[0-9]+$/, "Account Number must contain only numbers")}
                  onKeyDown={preventSpecialCharKeyDownInTextfield}

                />
                <Input
                  name="ifscCode"
                  label="IFSC Code"
                  placeholder="Enter IFSC Code"
                  required
                  disableAutoCapitalize
                  validationSchema={yup.string().trim().required("IFSC Code is required")}
                  onKeyDown={preventSpecialCharKeyDownInTextfield}
                />
                <Input
                  name="accountHolderName"
                  label="Account Holder Name"
                  placeholder="Enter Account Holder Name"
                  required
                  validationSchema={yup
                    .string()
                    .trim()
                    .required("Account Holder Name is required")}
                  onKeyDown={preventSpecialCharKeyAndNumbersDownInTextfield}
                />
                <Input
                  name="branchName"
                  label="Branch Name"
                  placeholder="Enter Branch Name"
                  required
                  validationSchema={yup.string().trim().required("Branch Name is required")}
                  onKeyDown={preventSpecialCharKeyAndNumbersDownInTextfield}
                />
                <Input
                  name="accountType"
                  label="Account Type"
                  placeholder="Savings/Current"
                  required
                  validationSchema={yup.string().trim().required("Account Type is required")}
                  onKeyDown={preventSpecialCharKeyAndNumbersDownInTextfield}
                />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={handleCancelClick}
                  className="ms-3 text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-900 focus:ring-4 focus:outline-none focus:ring-gray-200 font-medium rounded-lg text-sm px-5 py-2.5 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600 dark:hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={(!!bankDetails && !isDirty) || isSubmitting}
                  className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 ms-3 disabled:opacity-50  disabled:hover:bg-blue-700"
                >
                  {bankDetails ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </FormProvider>
        </Modal>
      )}
    </>
  );
};

export default BankDetails;